import { useState } from 'react';
import { clsx } from 'clsx';
import { Button } from '@/shared/ui/Button';
import { formatDate } from '@/shared/utils/format';
import type { CommunityOption, Pick } from '@/modules/community/api/communityApi';
import { metaFor } from '@/modules/community/components/pickMeta';
import { pickInitials } from '../utils/joePicks';
import styles from './JoeWeekPicks.module.css';

const AVATAR_CLASS: Record<Pick['kind'], string> = {
  partner: styles.avatarPartner,
  nontraditional_partner: styles.avatarNontrad,
  event: styles.avatarEvent,
};

const SOURCE_LABEL: Record<string, string> = {
  npi: 'NPI registry',
  places: 'Google Maps',
  ical: 'calendar feed',
  jsonld: 'events page',
};

/**
 * One of Joe's picks as a single decision row: who, where, why, and the three choices. The
 * suggested touch and the source sit behind "Details" so the list stays scannable; "Not for me"
 * asks why in place, the same reasons as the Community page.
 */
export function JoePickRow({
  pick,
  reasons,
  busy,
  onPlan,
  onSnooze,
  onDismiss,
}: {
  pick: Pick;
  reasons: CommunityOption[];
  busy: boolean;
  onPlan: (pick: Pick) => void;
  onSnooze: (pick: Pick) => void;
  onDismiss: (pick: Pick, reason: string) => void;
}) {
  const [details, setDetails] = useState(false);
  const [passing, setPassing] = useState(false);
  const offered = reasons.filter((r) => pick.reasons.includes(r.key));
  const isEvent = pick.kind === 'event';
  return (
    <li className={styles.pickRow}>
      <div className={styles.pickMain}>
        <span className={clsx(styles.avatar, AVATAR_CLASS[pick.kind])} aria-hidden="true">
          {pickInitials(pick.name)}
        </span>
        <div className={styles.pickText}>
          <div className={styles.pickNameRow}>
            <span className={styles.pickName}>{pick.name}</span>
            {pick.isNew && <span className={styles.newBadge}>New in your area</span>}
            {pick.state === 'snoozed' && <span className={styles.backBadge}>Back from snooze</span>}
            {pick.hasCost && <span className={styles.costBadge}>Costs money: your call</span>}
          </div>
          <div className={styles.pickMeta}>
            {isEvent ? metaFor(pick) : `${pick.categoryLabel} · ${pick.distanceMi} mi`}
            {pick.org ? ` · ${pick.org}` : ''}
          </div>
          <div className={styles.pickWhy}>{pick.why}</div>
          <button type="button" className={styles.detailsToggle} aria-expanded={details} onClick={() => setDetails((v) => !v)}>
            {details ? 'Hide details' : pick.vendorInfo || pick.boothDeadline ? 'Details: booth cost, deadline, suggested touch and source' : 'Details: suggested touch and source'}
          </button>
        </div>
        <div className={styles.pickActions}>
          <Button variant="primary" size="sm" disabled={busy} onClick={() => onPlan(pick)}>
            {isEvent ? 'Add to my plans' : 'Plan a touch'}
          </Button>
          <Button variant="secondary" size="sm" disabled={busy} onClick={() => onSnooze(pick)}>
            {pick.state === 'snoozed' ? 'Snooze again' : 'Snooze 30 days'}
          </Button>
          <Button variant="ghost" size="sm" disabled={busy} aria-expanded={passing} onClick={() => setPassing((v) => !v)}>
            Not for me
          </Button>
        </div>
      </div>
      {details && (
        <dl className={styles.details}>
          <div>
            <dt>Joe suggests</dt>
            <dd>{pick.suggestedTouchLabel}</dd>
          </div>
          <div>
            <dt>Source</dt>
            <dd>
              {SOURCE_LABEL[pick.source] ?? 'Added by you'}, verified {formatDate(pick.lastVerified)}
            </dd>
          </div>
          {pick.vendorInfo?.cost && (
            <div>
              <dt>Cost, per the event page</dt>
              <dd>"{pick.vendorInfo.cost}"</dd>
            </div>
          )}
          {pick.boothDeadline && (
            <div>
              <dt>Vendor or booth deadline</dt>
              <dd>
                {formatDate(pick.boothDeadline, 'MMM d')}
                {pick.vendorInfo?.deadlineText ? ` ("${pick.vendorInfo.deadlineText}")` : ''}
              </dd>
            </div>
          )}
          {pick.vendorInfo?.attendance && (
            <div>
              <dt>Attendance, per the page</dt>
              <dd>"{pick.vendorInfo.attendance}"</dd>
            </div>
          )}
          {pick.vendorInfo?.pastVendors[0] && (
            <div>
              <dt>Past vendors</dt>
              <dd>"{pick.vendorInfo.pastVendors[0]}"</dd>
            </div>
          )}
          {pick.vendorInfo?.registrationUrl && (
            <div>
              <dt>Application</dt>
              <dd>
                <a href={pick.vendorInfo.registrationUrl} target="_blank" rel="noreferrer">
                  Vendor or sponsor application
                </a>
              </dd>
            </div>
          )}
          {pick.sourceUrl && (
            <div>
              <dt>Listing</dt>
              <dd>
                <a href={pick.sourceUrl} target="_blank" rel="noreferrer">
                  Open listing
                </a>
              </dd>
            </div>
          )}
        </dl>
      )}
      {passing && (
        <div className={styles.passPanel}>
          <div className={styles.passTitle}>Why not? Joe will pick better next month.</div>
          <div className={styles.chips}>
            {offered.map((r) => (
              <button key={r.key} type="button" className={styles.chip} disabled={busy} onClick={() => onDismiss(pick, r.key)}>
                {r.label}
              </button>
            ))}
            <Button variant="ghost" size="sm" onClick={() => setPassing(false)}>
              Cancel
            </Button>
          </div>
        </div>
      )}
    </li>
  );
}
